import { NvModal } from '@simwood/nv-react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVisibleExtensionMenu } from '../../../shared/reducers/client';
import {
  getForwardingRules,
  removeForwardingRule,
  saveForwardingRule,
} from '../../../shared/services/forwardingRulesService';
import {
  ApiForwardingRule,
  ApiForwardingRuleCreate,
} from '../../../shared/types/ForwardingRule';
import { ApiPhoneExtensionWithSip } from '../../../shared/types/PhoneExtension';
import { StoreDispatch, StoreState } from '../../store';
import ForwardingRuleEdit from './ForwardingRuleEdit';
import ForwardingRulesList from './ForwardingRulesList';

interface Props {
  extension: ApiPhoneExtensionWithSip;
}

const ForwardingRulesModal: React.FC<Props> = ({ extension }) => {
  const dispatch = useDispatch<StoreDispatch>();
  const [forwardingRules, setForwardingRules] = useState<ApiForwardingRule[]>(
    [],
  );
  const activeCustomerId = useSelector(
    (state: StoreState) => state.client.activeCustomer?.id,
  );

  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');

  const [forwardingRule, setForwardingRule] = useState<
    ApiForwardingRuleCreate | undefined
  >(undefined);
  const [isFetching, setIsFetching] = useState(false);

  const onCancelClicked = useCallback(() => {
    dispatch(setVisibleExtensionMenu(undefined));
  }, [dispatch]);

  const toggleForwardingRuleEnabled = useCallback(
    async (fr: ApiForwardingRule) => {
      try {
        const res = await saveForwardingRule(activeCustomerId!, extension.id, {
          ...fr,
          enabled: !fr.enabled,
        });
        setForwardingRules((rules) => {
          const idx = rules.findIndex((fr) => fr.id === res.id);

          return [...rules.slice(0, idx), res, ...rules.slice(idx + 1)];
        });
      } catch (err) {
        console.error(err);
      }
    },
    [activeCustomerId, extension.id],
  );

  const deleteForwardingRule = useCallback(
    async (fr: ApiForwardingRule) => {
      try {
        await removeForwardingRule(activeCustomerId!, extension.id, fr.id);
        setForwardingRules((rules) =>
          rules.filter((rule) => rule.id !== fr.id),
        );
      } catch (err) {
        console.error(err);
      }
    },
    [activeCustomerId, extension.id],
  );

  const onEditModalClosed = useCallback(() => {
    setMode('list');
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFetching(true);
    getForwardingRules(activeCustomerId!, extension.id)
      .then((res) => {
        setForwardingRules(res);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [activeCustomerId, extension.id]);

  return (
    <NvModal.Dialog
      size='large'
      title='Forwarding Rules'
      open={true}
      setOpen={(open) => {
        if (!open) {
          onCancelClicked();
        }
      }}
    >
      <ForwardingRulesList
        forwardingRules={forwardingRules}
        isFetching={isFetching}
        onCardClick={(fr) => {
          setForwardingRule(fr);
          setMode('edit');
        }}
        toggleForwardingRuleEnabled={toggleForwardingRuleEnabled}
        deleteForwardingRule={deleteForwardingRule}
        onAddClick={() => {
          setForwardingRule(undefined);
          setMode('create');
        }}
      />

      {mode !== 'list' && (
        <ForwardingRuleEdit
          extension={extension}
          forwardingRule={forwardingRule}
          isCreate={mode === 'create'}
          onClose={onEditModalClosed}
          setForwardingRules={setForwardingRules}
          // onChange={(fr, v) => {
          //   setForwardingRule(fr);
          //   setValid(v);
          // }}
        />
      )}
    </NvModal.Dialog>
  );
};

export default ForwardingRulesModal;
